import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import DestinationItem from '../../items/DestinationItem';
import ImageHeader from '../../elements/ImageHeader';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import ItemContainer from '../../items/DestinationItem/atoms/ItemContainer';

function DestinationList(props) {
  const keyPress = {
    ARROWLEFT: 37,
    ARROWUP: 38,
    ARROWRIGHT: 39,
    ARROWDOWN: 40,
    TAB: 9
  };
  const arrayRef = [];
  const {
    data,
    image,
    showDefaultImage,
    imageProps,
    destinationItem,
    destinationColumn,
    onClickSeeMore,
    colWidth,
    paddingItem,
    columnNumbers,
    showCityName,
    label,
    labelSecondary,
    imageAlt,
    siteEdition,
    seeMoreBasicUrl,
    oneColumn,
    imageContentSettings,
    integrationSettings
  } = props;
  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const componentClassName = columnNumbers === 3 ? 'fr-destination-item-three-columns' : '';

  function handleFocusDestinationList(e, i) {
    // TODO: This props comes by parameter
    const step = 3;
    switch (e.keyCode) {
      case keyPress.ARROWLEFT: {
        if (arrayRef[i - 1]) {
          arrayRef[i - 1].focus();
        }
        break;
      }
      case keyPress.ARROWUP: {
        if (arrayRef[i - step]) {
          arrayRef[i - step].focus();
        }
        break;
      }
      case keyPress.ARROWRIGHT:
        if (arrayRef[i + 1]) {
          arrayRef[i + 1].focus();
        }
        break;
      case keyPress.ARROWDOWN:
        if (arrayRef[i + step]) {
          arrayRef[i + step].focus();
        }
        break;
      default:
        break;
    }
    if (!keyPress.TAB) {
      e.preventDefault();
    }
  }

  function calculatePadding(index) {
    const obj = {};
    const columns = 12 / colWidth;
    if (columns > 1) {
      if (index % columns === 0) {
        obj.paddingLeft = paddingItem;
      } else if ((index + columns - 1) % columns === 0) {
        obj.paddingRight = paddingItem;
      } else {
        obj.paddingWidth = paddingItem;
      }
    }
    return obj;
  }

  return (
    <>
      {(image || (!image && showDefaultImage)) && (
        <Row {...imageContentSettings}>
          <Col>
            <ImageHeader focusable {...imageProps} image={image} alt={imageAlt}>
              <ItemContainer
                {...destinationItem.imageProps?.container}
                text={label}
                subText={labelSecondary}
              />
            </ImageHeader>
          </Col>
        </Row>
      )}
      <Row className={`${className} ${componentClassName}`} style={style}>
        {data.map((item, index) => (
          <Col
            key={`destination-list-${item.id}`}
            lg={colWidth}
            paddingBottom="2"
            {...calculatePadding(index + 1)}
            {...destinationColumn}
          >
            <DestinationItem
              {...destinationItem}
              onClickSeeMore={onClickSeeMore}
              iataCode={item.iataCode}
              name={item.name}
              image={item.image}
              fares={item.fares}
              focusReferences={(ref) => {
                arrayRef[index] = ref;
              }}
              onKeyDown={(e) => handleFocusDestinationList(e, index)}
              showImage={!(image || (!image && showDefaultImage))}
              columnNumbers={columnNumbers}
              oneColumn={oneColumn}
              showCityName={showCityName}
              siteEdition={siteEdition}
              seeMoreBasicUrl={seeMoreBasicUrl}
              integrationSettings={integrationSettings}
            />
          </Col>
        ))}
      </Row>
    </>
  );
}

DestinationList.propTypes = {
  image: PropTypes.string,
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  imageAlt: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  destinationItem: PropTypes.objectOf(PropTypes.any),
  destinationColumn: PropTypes.objectOf(PropTypes.any),
  colWidth: PropTypes.string,
  /**
   * Settings to the imageContentSettings.
   */
  imageContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings to the image.
   */
  imageProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to translate the component
   */
  columnNumbers: PropTypes.number,
  /**
   * Show or hide the city name
   */
  showCityName: PropTypes.bool,
  /**
   * Origin City label
   */
  label: PropTypes.string,
  /**
   * Origin City iataCode
   */
  labelSecondary: PropTypes.string,
  /**
   * Site edition of the component
   */
  siteEdition: PropTypes.string,
  /**
   * Url base for component see more
   */
  seeMoreBasicUrl: PropTypes.string,
  /**
   * Show list in one column
   */
  oneColumn: PropTypes.bool,
  /**
   * Handle to be called when seeMore is Clicked
   */
  onClickSeeMore: PropTypes.func,
  /**
   * padding applied to items (this is used in calculatePadding func check above)
   */
  paddingItem: PropTypes.string,
  /**
   * Show default image when same city and not city image available
   */
  showDefaultImage: PropTypes.bool
};
DestinationList.defaultProps = {
  imageContentSettings: {},
  image: '',
  imageAlt: null,
  imageProps: {},
  data: [],
  destinationItem: {},
  destinationColumn: {},
  colWidth: '4',
  columnNumbers: null,
  showCityName: true,
  label: null,
  labelSecondary: null,
  siteEdition: null,
  seeMoreBasicUrl: null,
  oneColumn: true,
  onClickSeeMore: null,
  paddingItem: '1',
  showDefaultImage: false
};
DestinationList.componentName = 'DestinationList';

export default React.memo(
  DestinationList,
  (prevProps, nextProps) => prevProps.data === nextProps.data
);
